import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink } from "lucide-react";
import { TokenCard } from "@/components/TokenCard";
import type { UndervaluedGem } from "@/hooks/useUndervaluedGems";

const riskTone = (r?: string | null) =>
  r === "Low" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
  : r === "High" ? "bg-rose-500/15 text-rose-300 border-rose-500/30"
  : "bg-amber-500/15 text-amber-300 border-amber-500/30";

const fmtAud = (n: number | null) => {
  if (n == null) return "—";
  if (n < 1) return `A$${n.toFixed(4)}`;
  if (n < 100) return `A$${n.toFixed(2)}`;
  return `A$${Math.round(n).toLocaleString()}`;
};

const fmtMcap = (n: number | null) => {
  if (!n) return "—";
  if (n >= 1e9) return `A$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `A$${(n / 1e6).toFixed(1)}M`;
  return `A$${Math.round(n).toLocaleString()}`;
};

/**
 * `key_metrics` arrives as a pipe-delimited string, e.g.
 * "MCap A$120M | Vol24h A$4.2M | 7d +6.1% | Sector: DeFi".
 */
const parseKeyMetrics = (raw: string | null) => {
  const parts = (raw || "").split("|").map((p) => p.trim());
  const find = (re: RegExp) => {
    const hit = parts.find((p) => re.test(p));
    return hit ? hit.replace(re, "").trim() : null;
  };
  return {
    volume: find(/^vol\s*24h[:\s]*/i),
    change7d: find(/^7d[:\s]*/i),
    sectors: (find(/^sectors?[:\s]*/i) || "").split(",").map(x => x.trim()).filter(Boolean).slice(0, 2),
  };
};

const parsePct = (raw: string | null) => {
  if (!raw) return null;
  const n = parseFloat(raw.replace(/[^0-9.+-]/g, ""));
  return isFinite(n) ? n : null;
};

interface Props {
  gem: UndervaluedGem;
  hero?: boolean;
}

/** Undervalued Gems view of the shared TokenCard. */
export const UndervaluedGemCard = ({ gem, hero = false }: Props) => {
  const metrics = parseKeyMetrics(gem.key_metrics);
  const tags = [gem.category, ...metrics.sectors].filter(
    (t, i, arr): t is string => !!t && arr.indexOf(t) === i,
  );

  return (
    <TokenCard
      hero={hero}
      rank={gem.rank ?? undefined}
      symbol={gem.ticker || "?"}
      name={gem.name}
      price={fmtAud(gem.current_price_aud)}
      priceCaption={`Live AUD${gem.price_source ? ` · ${gem.price_source}` : ""}`}
      changes={[{ label: "7d", value: parsePct(metrics.change7d) }]}
      tags={tags}
      stats={[
        { label: "Market cap", value: fmtMcap(gem.market_cap) },
        { label: "Vol 24h", value: metrics.volume || "—" },
      ]}
      reasoning={
        gem.rationale && gem.rationale.length > 0 ? (
          <ul className="space-y-1.5">
            {gem.rationale.slice(0, 4).map((r, i) => (
              <li key={i} className="flex gap-2 text-foreground/85">
                <span className="text-primary mt-0.5 shrink-0">▸</span>
                <span className="leading-snug">{r}</span>
              </li>
            ))}
          </ul>
        ) : null
      }
      footer={
        <div className="flex items-center justify-between gap-3">
          <Badge variant="outline" className={`${riskTone(gem.risk_level)} text-xs`}>
            {gem.risk_level || "Medium"} risk
          </Badge>
          <Button asChild size="sm" className="bg-gradient-to-r from-primary to-purple-500 hover:opacity-90">
            <a href={gem.swyftx_url || "#"} target="_blank" rel="noopener noreferrer">
              View on Swyftx <ExternalLink className="w-3.5 h-3.5 ml-1" />
            </a>
          </Button>
        </div>
      }
    >
      {gem.key_metrics && (
        <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
          <div className="type-eyebrow text-primary mb-1.5">Key metrics</div>
          <p className="text-xs font-mono leading-relaxed text-foreground/90">{gem.key_metrics}</p>
        </div>
      )}
    </TokenCard>
  );
};
